import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router';
import { RequireAdmin } from './auth/RequireAdmin';
import { Loading } from './components/ui';
import { AppLayout } from './routes/AppLayout';
import { DashboardPage } from './routes/DashboardPage';
import { ErrorPage } from './routes/ErrorPage';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { importOrReload } from './lib/reload-once';

function page<T extends Record<string, ComponentType<never>>>(load: () => Promise<T>, name: keyof T) {
  return lazy(async () => ({ default: (await importOrReload(load))[name] as ComponentType }));
}

const ProductsPage = page(() => import('./features/catalog/ProductsPage'), 'ProductsPage');
const ProductFormPage = page(() => import('./features/catalog/ProductFormPage'), 'ProductFormPage');
const TaxonomyPage = page(() => import('./features/catalog/TaxonomyPage'), 'TaxonomyPage');
const TaxonomyFormPage = lazy(async () => ({ default: (await importOrReload(() => import('./features/catalog/TaxonomyFormPage'))).TaxonomyFormPage }));
const SettingsPage = page(() => import('./features/settings/SettingsPage'), 'SettingsPage');
const HomeSettingsPage = page(() => import('./features/home/HomeSettingsPage'), 'HomeSettingsPage');
const ContentPage = page(() => import('./features/content/ContentPage'), 'ContentPage');
const PostFormPage = page(() => import('./features/content/PostFormPage'), 'PostFormPage');
const PageFormPage = page(() => import('./features/content/PageFormPage'), 'PageFormPage');
const RedirectsPage = page(() => import('./features/redirects/RedirectsPage'), 'RedirectsPage');
const AuditPage = page(() => import('./features/audit/AuditPage'), 'AuditPage');
const OrdersPage = page(() => import('./features/orders/OrdersPage'), 'OrdersPage');
const OrderDetailPage = page(() => import('./features/orders/OrderDetailPage'), 'OrderDetailPage');
const CustomersPage = page(() => import('./features/customers/CustomersPage'), 'CustomersPage');
const LeadsPage = page(() => import('./features/leads/LeadsPage'), 'LeadsPage');
const CustomerDetailPage = page(() => import('./features/customers/CustomerDetailPage'), 'CustomerDetailPage');

const s = (node: ReactNode) => <Suspense fallback={<Loading />}>{node}</Suspense>;

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    errorElement: <ErrorPage />,
    element: (
      <RequireAdmin>
        <AppLayout />
      </RequireAdmin>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'products', element: s(<ProductsPage />) },
      { path: 'products/new', element: s(<ProductFormPage />) },
      { path: 'products/:id', element: s(<ProductFormPage />) },
      { path: 'categories', element: s(<TaxonomyPage />) },
      { path: 'categories/new', element: s(<TaxonomyFormPage kind="category" />) },
      { path: 'categories/:id', element: s(<TaxonomyFormPage kind="category" />) },
      { path: 'collections/new', element: s(<TaxonomyFormPage kind="collection" />) },
      { path: 'collections/:id', element: s(<TaxonomyFormPage kind="collection" />) },
      { path: 'home', element: s(<HomeSettingsPage />) },
      { path: 'content', element: s(<ContentPage />) },
      { path: 'posts/new', element: s(<PostFormPage />) },
      { path: 'posts/:id', element: s(<PostFormPage />) },
      { path: 'pages/:id', element: s(<PageFormPage />) },
      { path: 'settings', element: s(<SettingsPage />) },
      { path: 'redirects', element: s(<RedirectsPage />) },
      { path: 'audit', element: s(<AuditPage />) },
      { path: 'orders', element: s(<OrdersPage />) },
      { path: 'orders/:number', element: s(<OrderDetailPage />) },
      { path: 'customers', element: s(<CustomersPage />) },
      { path: 'customers/:phone', element: s(<CustomerDetailPage />) },
      { path: 'leads', element: s(<LeadsPage />) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
